<template>
  <div class="py-8 bg-[#F9F7EE] 3xl:px-0 px-4">
    <div class="hidden mb-4 text-center lg:block">
      <h2 class="mb-4 h2" v-html="mainHeading"></h2>
      <h3 class="max-w-3xl px-4 mx-auto mb-3 h4 font-open-sans" v-html="subHeading"></h3>
    </div>
    <div class="grid items-center max-w-screen-xl grid-cols-1 mx-auto md:grid lg:grid-cols-12 xl-max:mt-0 lg:gap-x-6">
      <div class="flex flex-col gap-6 max-w-screen-sm mx-auto xl-max:order-[2] xl-max:mb-8 md-max:px-6 lg:col-span-4">
        <div v-for="(step, index) in steps" :key="index" class="flex flex-row items-center justify-center gap-3">
          <div class="w-24">
            <nuxt-img 
              :alt="step.iconAlt" 
              width="80" 
              height="80" 
              loading="lazy" 
              format="webp" 
              :src="step.iconSrc" 
              class="mx-auto lg:ml-0"
            />
          </div>
          <div class="w-2/3 decorate">
            <p class="h3" role="heading" :aria-level="index + 1" :aria-label="'Step ' + (index + 1)" v-html="step.title"></p>
            <p class="textp" v-html="step.description"></p>
            <button v-if="step.button" @click="scrollTo(step.button.href, scrollPosition())" class="w-full py-1 mt-2 mb-4 font-bold font-ww-medium btn-red bg-lifeboost-ambassador md:mr-6 md:w-auto md:mb-0" v-html="step.button.text"></button>
          </div>
        </div>
        <div class="-mt-5 text-center md:mb-10 font-italic lg:mb-0 sm:ml-8 md:text-left" v-html="optionalText"></div>
      </div>
      <div class="mt-6 lg:col-span-8 lg:order-first xl-max:mt-0 xl-max:mb-6 md:mt-0">
        <video ref="video" class="w-full mx-auto rounded-none vue-video md:rounded-3xl aspect-auto" loop="false" preload="none" muted="false" autoplay="false" playsinline="true" controls="" :poster="videoPoster"></video>
      </div>
    </div>
  </div>
</template>

<script setup>

const props = defineProps({
  mainHeading: {
    type: String,
    default: 'How to Brew Your Lifeboost Magic'
  },
  subHeading: {
    type: String,
    default: "Follow our simple guide to crafting the perfect Lifeboost latte. From the first sip, you'll understand why this is more than coffee—it's a ritual"
  },
  steps: {
    type: Array,
    default: () => [
      {
        iconSrc: 'assets/images/step-icon-1.svg',
        iconAlt: 'icon-1',
        title: 'Step 1',
        description: 'Brew your Lifeboost Cognition Mushroom Coffee (hot or cold - you choose!)',
        button: {
          href: 'the-products',
          text: 'Shop Now'
        }
      },
      {
        iconSrc: 'assets/images/step-icon-2.svg',
        iconAlt: 'icon-2',
        title: 'Step 2',
        description: 'Add coffee to your blender.'
      },
      {
        iconSrc: 'assets/images/step-icon-3.svg',
        iconAlt: 'icon-3',
        title: 'Step 3',
        description: 'Add Vit D + K drops, Everlo drops and Mitocreamer (follow the dosage on the package)'
      },
      {
        iconSrc: 'assets/images/step-icon-4.svg',
        iconAlt: 'icon-4',
        title: 'Step 4',
        description: 'Blend together & enjoy!'
      }
    ]
  },
  optionalText: {
    type: String,
    default: 'Optional: for an even creamier latte, add a splash of organic heavy whipping cream'
  },
  videoPoster: {
    type: String,
    default: '/assets/images/image-poster.png'
  },
  videoSrc: {
    type: String,
    default: 'https://cdn.shopify.com/videos/c/o/v/3cb5da9027b9425a90c6e9cde9391246.mp4'
  }
});

const video = ref(null);
const observer = ref(null);
const hasPlayed = ref(false);

const handleIntersection = (entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting && !hasPlayed.value) {
      const videoElement = video.value;
      if (videoElement) {
        videoElement.src = props.videoSrc;
        videoElement.onloadeddata = () => {
          videoElement.play();
          hasPlayed.value = true;
          observer.value.unobserve(videoElement);
        };
      }
    }
  });
};

onMounted(() => {
  observer.value = new IntersectionObserver(handleIntersection, {
    threshold: 0.5
  });
  if (video.value) {
    observer.value.observe(video.value);
  }
});

onBeforeUnmount(() => {
  if (observer.value && video.value) {
    observer.value.unobserve(video.value);
  }
});

defineBuilderComponent({
  friendlyName: "Home Steps",
  name: 'Home Steps',
  canHaveChildren: true,
  inputs: [
    {
      name: 'mainHeading',
      type: 'richText',
      defaultValue: 'How to Brew Your Lifeboost Magic'
    },
    {
      name: 'subHeading',
      type: 'richText',
      defaultValue: "Follow our simple guide to crafting the perfect Lifeboost latte. From the first sip, you'll understand why this is more than coffee—it's a ritual"
    },
    {
      name: 'steps',
      type: 'list',
      subFields: [
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['svg', 'webp'],
          required: true
        },
        {
          name: 'iconAlt',
          type: 'text',
          required: true
        },
        {
          name: 'title',
          type: 'richText',
          required: true
        },
        {
          name: 'description',
          type: 'richText',
          required: true
        },
        {
          name: 'button',
          type: 'object',
          subFields: [
            {
              name: 'href',
              type: 'text',
              required: true
            },
            {
              name: 'text',
              type: 'richText',
              required: true
            }
          ],
          required: false
        }
      ],
      defaultValue: [
        {
          iconSrc: 'assets/images/step-icon-1.svg',
          iconAlt: 'icon-1',
          title: 'Step 1',
          description: 'Brew your Lifeboost Cognition Mushroom Coffee (hot or cold - you choose!)',
          button: {
            href: 'the-products',
            text: 'Shop Now'
          }
        },
        {
          iconSrc: 'assets/images/step-icon-2.svg',
          iconAlt: 'icon-2',
          title: 'Step 2',
          description: 'Add coffee to your blender.'
        },
        {
          iconSrc: 'assets/images/step-icon-3.svg',
          iconAlt: 'icon-3',
          title: 'Step 3',
          description: 'Add Vit D + K drops, Everlo drops and Mitocreamer (follow the dosage on the package)'
        },
        {
          iconSrc: 'assets/images/step-icon-4.svg',
          iconAlt: 'icon-4',
          title: 'Step 4',
          description: 'Blend together & enjoy!'
        }
      ]
    },
    {
      name: 'optionalText',
      type: 'richText',
      defaultValue: 'Optional: for an even creamier latte, add a splash of organic heavy whipping cream'
    },
    {
      name: 'videoPoster',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'svg', 'png', 'webp'],
      defaultValue: '/assets/images/image-poster.png'
    },
    {
      name: 'videoSrc',
      type: 'text',
      defaultValue: 'https://cdn.shopify.com/videos/c/o/v/3cb5da9027b9425a90c6e9cde9391246.mp4'
    }
  ]
});

</script>

<style scoped>
/* Your styles here */
</style>